<template>
  <div class="p-6 bg-gray-100 min-h-screen">
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-gray-800">日志管理</h1>
        <div class="flex space-x-4">
          <div class="relative">
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="搜索操作用户、模块..." 
              class="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
            <svg class="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
          <select 
            v-model="typeFilter" 
            class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">全部类型</option>
            <option value="login">登录日志</option>
            <option value="operation">操作日志</option>
            <option value="error">错误日志</option>
          </select>
          <div class="flex items-center space-x-2">
            <div class="w-28">
              <input 
                type="date" 
                v-model="startDate" 
                class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent w-full"
              />
            </div>
            <span class="text-gray-500">至</span>
            <div class="w-28">
              <input 
                type="date" 
                v-model="endDate" 
                class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent w-full"
              />
            </div>
          </div>
          <button 
            @click="exportLogs"
            class="px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors duration-300 flex items-center"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
            </svg>
            导出日志
          </button>
        </div>
      </div>

      <!-- 日志列表 -->
      <div class="overflow-hidden shadow-md border border-gray-200 rounded-lg">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">操作用户</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">操作类型</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">操作模块</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">IP地址</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">状态</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">操作时间</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">操作</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(log, index) in filteredLogs" :key="index" class="hover:bg-gray-50 transition-colors duration-200">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ log.id }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm font-medium text-gray-900">{{ log.username }}</div>
                <div class="text-xs text-gray-500">{{ log.userRole }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span 
                  :class="[
                    'px-2 py-1 inline-flex text-xs font-semibold rounded-full',
                    log.type === 'login' ? 'bg-blue-100 text-blue-800' :
                    log.type === 'operation' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  ]"
                >
                  {{ typeText(log.type) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ log.module }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ log.ip }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span 
                  :class="[
                    'px-2 py-1 inline-flex text-xs font-semibold rounded-full',
                    log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  ]"
                >
                  {{ log.status === 'success' ? '成功' : '失败' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ log.timestamp }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <button 
                  @click="viewLogDetail(log)" 
                  class="text-blue-600 hover:text-blue-800"
                >
                  详情
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <div class="flex justify-between items-center mt-6">
        <p class="text-sm text-gray-700">
          显示 <span class="font-medium">{{ (currentPage - 1) * itemsPerPage + 1 }}</span> 至 
          <span class="font-medium">{{ Math.min(currentPage * itemsPerPage, logs.length) }}</span> 条，
          共 <span class="font-medium">{{ logs.length }}</span> 条
        </p>
        <div class="flex space-x-2">
          <button 
            @click="previousPage" 
            :disabled="currentPage === 1" 
            :class="[
              'px-3 py-1 border rounded-md',
              currentPage === 1 ? 'cursor-not-allowed text-gray-400 border-gray-200' : 'border-gray-300 text-gray-700'
            ]"
          >
            上一页
          </button>
          <button 
            v-for="page in totalPages"
            :key="page"
            @click="goToPage(page)" 
            :class="[
              'px-3 py-1 border rounded-md', 
              currentPage === page ? 'bg-red-500 text-white border-red-500' : 'border-gray-300 text-gray-700'
            ]"
          >
            {{ page }}
          </button>
          <button 
            @click="nextPage" 
            :disabled="currentPage === totalPages" 
            :class="[
              'px-3 py-1 border rounded-md',
              currentPage === totalPages ? 'cursor-not-allowed text-gray-400 border-gray-200' : 'border-gray-300 text-gray-700'
            ]"
          >
            下一页
          </button>
        </div>
      </div>
    </div>

    <!-- 日志详情对话框 -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-2xl w-full">
        <!-- 对话框头部 -->
        <div class="flex justify-between items-center border-b px-6 py-4">
          <h3 class="text-lg font-semibold text-gray-800">日志详情</h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-500">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <!-- 对话框内容 -->
        <div class="px-6 py-4">
          <div v-if="selectedLog" class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <p class="text-sm text-gray-500">日志ID</p>
                <p class="font-medium">{{ selectedLog.id }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-500">操作时间</p>
                <p class="font-medium">{{ selectedLog.timestamp }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-500">操作用户</p>
                <p class="font-medium">{{ selectedLog.username }} ({{ selectedLog.userRole }})</p>
              </div>
              <div>
                <p class="text-sm text-gray-500">IP地址</p>
                <p class="font-medium">{{ selectedLog.ip }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-500">操作类型</p>
                <p class="font-medium">{{ typeText(selectedLog.type) }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-500">操作状态</p>
                <p class="font-medium">{{ selectedLog.status === 'success' ? '成功' : '失败' }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-500">操作模块</p>
                <p class="font-medium">{{ selectedLog.module }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-500">操作方法</p>
                <p class="font-medium">{{ selectedLog.method }}</p>
              </div>
            </div>
            <div>
              <p class="text-sm text-gray-500">操作描述</p>
              <p class="font-medium">{{ selectedLog.description }}</p>
            </div>
            <div v-if="selectedLog.parameters">
              <p class="text-sm text-gray-500">请求参数</p>
              <div class="bg-gray-50 p-3 rounded-md">
                <pre class="text-xs overflow-auto max-h-32">{{ JSON.stringify(selectedLog.parameters, null, 2) }}</pre>
              </div>
            </div>
            <div v-if="selectedLog.type === 'error'">
              <p class="text-sm text-gray-500">错误信息</p>
              <div class="bg-red-50 p-3 rounded-md">
                <pre class="text-xs overflow-auto max-h-32 text-red-700">{{ selectedLog.errorMessage }}</pre>
              </div>
            </div>
            <div v-if="selectedLog.userAgent">
              <p class="text-sm text-gray-500">用户代理</p>
              <p class="font-medium text-xs break-all">{{ selectedLog.userAgent }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LogManagement',
  data() {
    return {
      searchQuery: '',
      typeFilter: 'all',
      startDate: '',
      endDate: '',
      currentPage: 1,
      itemsPerPage: 10,
      showModal: false,
      selectedLog: null,
      
      // 模拟数据
      logs: [
        {
          id: 1001,
          username: '管理员',
          userRole: '系统管理员',
          type: 'login',
          module: '认证服务',
          method: 'login',
          description: '用户登录',
          ip: '192.168.1.100',
          status: 'success',
          timestamp: '2024-07-31 08:30:25',
          userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/115.0.0.0 Safari/537.36'
        },
        {
          id: 1002,
          username: '张经理',
          userRole: '部门管理员',
          type: 'operation',
          module: '积分审核',
          method: 'approvePoints',
          description: '审批积分申请',
          parameters: { applicationId: 12345, status: 'approved' },
          ip: '192.168.1.101',
          status: 'success',
          timestamp: '2024-07-31 09:15:46'
        },
        {
          id: 1003,
          username: '李四',
          userRole: '普通员工',
          type: 'operation',
          module: '积分上报',
          method: 'submitPoints',
          description: '提交积分申请',
          parameters: { indicatorId: 5, points: 10, description: '完成XYZ项目' },
          ip: '192.168.1.120',
          status: 'success',
          timestamp: '2024-07-31 10:05:12'
        },
        {
          id: 1004,
          username: '王五',
          userRole: '普通员工',
          type: 'error',
          module: '积分上报',
          method: 'submitPoints',
          description: '提交积分申请失败',
          parameters: { indicatorId: 8, points: 15, description: '参加培训' },
          errorMessage: 'java.lang.NullPointerException: Attachment cannot be null\n\tat com.icbc.sd.service.impl.PointsServiceImpl.submitPoints(PointsServiceImpl.java:125)',
          ip: '192.168.1.135',
          status: 'failed',
          timestamp: '2024-07-31 11:22:08'
        },
        {
          id: 1005,
          username: '管理员',
          userRole: '系统管理员',
          type: 'operation',
          module: '用户管理',
          method: 'createUser',
          description: '创建新用户',
          parameters: { username: 'zhangsan', name: '张三', role: 'employee', department: 'tech' },
          ip: '192.168.1.100',
          status: 'success',
          timestamp: '2024-07-31 13:45:30'
        },
        {
          id: 1006,
          username: '赵六',
          userRole: '普通员工',
          type: 'login',
          module: '认证服务',
          method: 'login',
          description: '用户登录失败',
          ip: '192.168.1.140',
          status: 'failed',
          timestamp: '2024-07-31 14:12:55',
          userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 16_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/15.0 Mobile/15E148 Safari/604.1'
        },
        {
          id: 1007,
          username: '张经理',
          userRole: '部门管理员',
          type: 'operation',
          module: '部门管理',
          method: 'updateDepartment',
          description: '更新部门信息',
          parameters: { departmentId: 3, name: '技术研发部', managerName: '张经理' },
          ip: '192.168.1.101',
          status: 'success',
          timestamp: '2024-07-31 15:08:42'
        },
        {
          id: 1008,
          username: '管理员',
          userRole: '系统管理员',
          type: 'operation',
          module: '系统配置',
          method: 'updateSystemConfig',
          description: '更新系统配置',
          parameters: { configKey: 'reviewLevel', configValue: 'double' },
          ip: '192.168.1.100',
          status: 'success',
          timestamp: '2024-07-31 16:30:15'
        },
        {
          id: 1009,
          username: '李四',
          userRole: '普通员工',
          type: 'login',
          module: '认证服务',
          method: 'login',
          description: '用户登录',
          ip: '192.168.1.120',
          status: 'success',
          timestamp: '2024-07-31 17:05:33',
          userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/115.0.0.0 Safari/537.36'
        },
        {
          id: 1010,
          username: '系统',
          userRole: '系统',
          type: 'operation',
          module: '定时任务',
          method: 'generateMonthlyReport',
          description: '生成月度报表',
          ip: '127.0.0.1',
          status: 'success',
          timestamp: '2024-08-01 00:01:05'
        },
        {
          id: 1011,
          username: '系统',
          userRole: '系统',
          type: 'error',
          module: '定时任务',
          method: 'cleanupTempFiles',
          description: '清理临时文件失败',
          errorMessage: 'java.io.IOException: Permission denied\n\tat com.icbc.sd.service.impl.FileServiceImpl.cleanupTempFiles(FileServiceImpl.java:218)',
          ip: '127.0.0.1',
          status: 'failed',
          timestamp: '2024-08-01 00:05:12'
        },
        {
          id: 1012,
          username: '管理员',
          userRole: '系统管理员',
          type: 'login',
          module: '认证服务',
          method: 'login',
          description: '用户登录',
          ip: '192.168.1.100',
          status: 'success',
          timestamp: '2024-08-01 08:15:45',
          userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/115.0.0.0 Safari/537.36'
        }
      ]
    }
  },
  computed: {
    filteredLogs() {
      let result = this.logs;
      
      // 搜索过滤
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(log => 
          log.username.toLowerCase().includes(query) || 
          log.module.toLowerCase().includes(query) ||
          log.description.toLowerCase().includes(query)
        );
      }
      
      // 类型过滤
      if (this.typeFilter !== 'all') {
        result = result.filter(log => log.type === this.typeFilter);
      }
      
      // 日期过滤
      if (this.startDate && this.endDate) {
        const startDate = new Date(this.startDate);
        const endDate = new Date(this.endDate);
        endDate.setHours(23, 59, 59, 999); // 设置为当天的最后一毫秒
        
        result = result.filter(log => {
          const logDate = new Date(log.timestamp);
          return logDate >= startDate && logDate <= endDate;
        });
      } else if (this.startDate) {
        const startDate = new Date(this.startDate);
        result = result.filter(log => {
          const logDate = new Date(log.timestamp);
          return logDate >= startDate;
        });
      } else if (this.endDate) {
        const endDate = new Date(this.endDate);
        endDate.setHours(23, 59, 59, 999);
        result = result.filter(log => {
          const logDate = new Date(log.timestamp);
          return logDate <= endDate;
        });
      }
      
      // 分页处理
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return result.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.logs.length / this.itemsPerPage);
    }
  },
  methods: {
    typeText(type) {
      const typeMap = {
        'login': '登录日志',
        'operation': '操作日志',
        'error': '错误日志'
      };
      return typeMap[type] || type;
    },
    viewLogDetail(log) {
      this.selectedLog = log;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.selectedLog = null;
    },
    exportLogs() {
      alert('日志导出功能正在开发中，敬请期待!');
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
      }
    },
    goToPage(page) {
      this.currentPage = page;
    }
  }
}
</script> 